import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { LayoutDashboard, Building2, MapPin, Settings, QrCode } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { useDashboardData } from '../../hooks/useDashboardData';
import './DashboardLayout.css';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  requiresOrgAccess?: boolean;
  requiresSuperAdmin?: boolean;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
  { to: '/dashboard/organizations', label: 'Organizations', icon: <Building2 size={20} />, requiresOrgAccess: true },
  { to: '/dashboard/sites', label: 'Sites', icon: <MapPin size={20} /> },
  { to: '/dashboard/provision', label: 'Add Device', icon: <QrCode size={20} />, requiresSuperAdmin: true },
  { to: '/dashboard/settings', label: 'Settings', icon: <Settings size={20} /> },
];

export function DashboardLayout() {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { organizations, isSuperAdmin } = useDashboardData();

  const toggleSidebar = () => setSidebarExpanded((prev) => !prev);

  // Show the Organizations tab if the user is a SuperAdmin OR has any org-level role
  // (i.e. at least one organization where their role is not 'SiteUser')
  const hasOrgAccess = isSuperAdmin || organizations.some(o => o.role && o.role !== 'SiteUser');

  const visibleNavItems = navItems.filter(item => {
    if (item.requiresOrgAccess && !hasOrgAccess) {
      return false;
    }
    if (item.requiresSuperAdmin && !isSuperAdmin) {
      return false;
    }
    return true;
  });

  const handleNavLinkClick = () => {
    if (window.innerWidth <= 768) {
      setSidebarExpanded(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Header onMenuToggle={toggleSidebar} />

      <div className="dashboard-layout__body">
        {sidebarExpanded && (
          <div className="sidebar-backdrop" onClick={() => setSidebarExpanded(false)} />
        )}

        <aside
          className={`sidebar ${sidebarExpanded ? 'sidebar--expanded' : 'sidebar--collapsed'}`}
        >
          <nav className="sidebar__nav">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                state={item.to === '/dashboard/organizations' ? { fromSidebar: true } : undefined}
                end={item.to === '/dashboard'}
                className={({ isActive }) =>
                  `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
                }
                title={item.label}
                onClick={handleNavLinkClick}
              >
                <span className="sidebar__icon">{item.icon}</span>
                <span className="sidebar__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="dashboard-layout__main">
          <div className="dashboard-layout__content">
            <Outlet />
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
